/**
 * AddPaymentModal — used to record a manual payment against an invoice.
 *
 * Enforces the same rules as the backend:
 *   • cash / bank_transfer / check → proof file upload REQUIRED
 *   • credit_card / other          → proof optional
 *   • amount ≤ remaining balance
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  X, Loader2, Save, Upload, FileImage, CheckCircle2,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { floorAED } from '../../utils/money';
import { DirhamPrice } from '../../components/DirhamPrice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROOF_REQUIRED_METHODS = ['cash', 'bank_transfer', 'check'];

const METHODS = [
  { id: 'cash',          ar: 'نقدي',         en: 'Cash' },
  { id: 'bank_transfer', ar: 'تحويل بنكي',   en: 'Bank Transfer' },
  { id: 'check',         ar: 'شيك',          en: 'Check' },
  { id: 'credit_card',   ar: 'بطاقة ائتمان', en: 'Credit Card' },
  { id: 'other',         ar: 'أخرى',         en: 'Other' },
];

export default function AddPaymentModal({ invoice, onClose, onDone }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState(String(invoice.remaining_amount ?? invoice.total ?? ''));
  const [note, setNote] = useState('');
  const [proof, setProof] = useState(null);      // { file_id, filename } after upload
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const proofRequired = PROOF_REQUIRED_METHODS.includes(method);
  const amountNum = parseFloat(amount);
  const remaining = Number(invoice.remaining_amount ?? invoice.total ?? 0);

  const amountValid = amountNum > 0 && amountNum <= remaining + 0.01;
  const canSubmit = amountValid && (!proofRequired || proof);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/upload/file`, fd, {
        headers: { ...getAuthHeaders() },  // browser adds multipart boundary
      });
      setProof({ file_id: r.data.file_id, filename: r.data.filename });
      toast.success(t('تم رفع صورة الإثبات', 'Proof uploaded'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الرفع', 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/invoices/${invoice.id}/add-payment`,
        {
          amount: amountNum,
          method,
          proof_file_id: proof?.file_id || null,
          proof_filename: proof?.filename || null,
          note: note.trim(),
        },
        { headers: getAuthHeaders() },
      );
      toast.success(t('✓ تم تسجيل الدفعة', '✓ Payment recorded'));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التسجيل', 'Record failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
      />
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.94, opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={(e) => e.stopPropagation()}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
             data-testid="add-payment-modal">
          <div className="p-4 border-b border-white/10 flex items-center gap-3">
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-white/70"
                    data-testid="add-payment-close">
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h3 className="text-white font-bold">{t('تسجيل دفعة', 'Record Payment')} — INV-{invoice.invoice_no}</h3>
              <p className="text-white/50 text-xs">
                {invoice.customer?.name} · {t('المتبقي', 'Remaining')}:
                <span className="text-amber-400 font-mono ms-1.5"><DirhamPrice value={remaining} decimals={2} size="sm" /></span>
              </p>
            </div>
          </div>

          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Method */}
            <div>
              <label className="block text-white/70 text-xs mb-2">{t('طريقة الدفع', 'Method')}</label>
              <div className="grid grid-cols-3 gap-2">
                {METHODS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { setMethod(m.id); if (!PROOF_REQUIRED_METHODS.includes(m.id)) setProof(null); }}
                    className={`px-3 py-2 rounded-lg border text-sm text-start transition-colors ${
                      method === m.id
                        ? 'bg-[#d4af37]/10 border-[#d4af37] text-white font-medium'
                        : 'bg-[#050505] border-white/10 text-white/70 hover:border-white/30'
                    }`}
                    data-testid={`add-payment-method-${m.id}`}
                  >
                    {isAr ? m.ar : m.en}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-white/70 text-xs mb-1.5">
                {t('المبلغ', 'Amount')} *
              </label>
              <div className="flex gap-2">
                <Input type="number" step="0.01" min="0"
                       value={amount}
                       onChange={(e) => setAmount(e.target.value)}
                       className="bg-[#050505] border-white/10 text-white flex-1"
                       data-testid="add-payment-amount" />
                <Button
                  variant="outline"
                  onClick={() => setAmount(String(floorAED(remaining).toFixed(2)))}
                  className="border-white/10 bg-transparent text-white/70 hover:bg-white/5"
                  data-testid="add-payment-fill-remaining"
                >
                  {t('كامل المتبقي', 'Full remaining')}
                </Button>
              </div>
              {!amountValid && amount && (
                <div className="text-red-400 text-[11px] mt-1">
                  {amountNum <= 0
                    ? t('المبلغ يجب أن يكون أكبر من صفر', 'Amount must be > 0')
                    : t(`المبلغ يتجاوز المتبقي (${floorAED(remaining).toFixed(2)})`, `Amount exceeds remaining (${floorAED(remaining).toFixed(2)})`)}
                </div>
              )}
            </div>

            {/* Proof upload */}
            <div>
              <label className="block text-white/70 text-xs mb-1.5">
                {t('صورة الإثبات', 'Proof of payment')}
                {proofRequired && <span className="text-red-400 ms-1">*</span>}
                {proofRequired && (
                  <span className="text-amber-400/80 ms-2 text-[11px]">
                    {t('إلزامي', 'required')} — {method === 'cash' && t('إيداع في البنك/ATM', 'Bank/ATM deposit slip')}
                    {method === 'bank_transfer' && t('حوالة بنكية', 'Bank transfer receipt')}
                    {method === 'check' && t('صورة الشيك', 'Check image')}
                  </span>
                )}
              </label>

              <input ref={fileRef} type="file" accept="image/*,application/pdf"
                     className="hidden"
                     onChange={(e) => handleFile(e.target.files?.[0])}
                     data-testid="add-payment-file-input" />

              {proof ? (
                <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 flex items-center gap-3"
                     data-testid="add-payment-proof-uploaded">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-emerald-300 text-sm font-medium truncate">{proof.filename}</div>
                    <div className="text-emerald-200/60 text-xs">{t('تم رفع الملف', 'File uploaded')}</div>
                  </div>
                  <button onClick={() => { setProof(null); if (fileRef.current) fileRef.current.value = ''; }}
                          className="text-white/60 hover:text-white p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full p-4 rounded-lg border-2 border-dashed border-white/15 hover:border-[#d4af37]/40 flex flex-col items-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="add-payment-upload-btn"
                >
                  {uploading ? (
                    <><Loader2 className="w-6 h-6 animate-spin text-white/60" />
                      <span className="text-white/60 text-sm">{t('جارٍ الرفع...', 'Uploading...')}</span></>
                  ) : (
                    <><Upload className="w-6 h-6 text-white/40" />
                      <span className="text-white/60 text-sm">
                        {t('اضغط لرفع صورة أو PDF', 'Click to upload image or PDF')}
                      </span></>
                  )}
                </button>
              )}
            </div>

            {/* Note */}
            <div>
              <label className="block text-white/70 text-xs mb-1.5">{t('ملاحظات (اختياري)', 'Note (optional)')}</label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white"
                     placeholder={t('رقم مرجعي، تفاصيل التحويل...', 'Reference number, transfer details...')}
                     data-testid="add-payment-note" />
            </div>

            {/* Submit */}
            <Button onClick={submit} disabled={!canSubmit || submitting}
                    className="w-full bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold h-11 disabled:opacity-50"
                    data-testid="add-payment-submit">
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ التسجيل...', 'Recording...')}</>
                : <><Save className="w-4 h-4 me-2" />{t('تسجيل الدفعة', 'Record payment')}</>}
            </Button>
          </div>
        </div>
      </motion.div>
    </>
  );
}
